import UAParser from 'ua-parser-js';
import * as QueryString from 'query-string';

// Helper function to merge multiple objects
// Note that objects with higher indices will overwrite!
export function mergeObjects(arr) {
  const obj = {};
  for (let i = 0; i < arr.length; i++) {
    for (const prop in arr[i]) {
      obj[prop] = arr[i][prop];
    }
  }
  return obj;
}

/**
 * Helper function to filter out potential PII info
 *
 * Example params: ?a=b&c=d&email=abc@example.com
 * Returns: ?a=b&c=d&email=[REDACTED]
 */
export function filterUrl(url) {
  let path = '';
  let params = '';
  // to capture when PII is the first parameter
  // https://example.com/hello?password=abc&k=v
  if (url.indexOf('?') !== -1) {
    const values = url.split('?');
    path = values[0];
    params = values[1];
  } else {
    params = url;
  }

  // these are the common keys for PII
  const filteredParams = ['email', 'email_confirmation', 'password'];
  let queryArray = params.split('&');
  queryArray = queryArray.map(function(element) {
    const key_value = element.split('=');
    const key = key_value[0];
    if (filteredParams.indexOf(key) !== -1) {
      key_value[1] = '[REDACTED]';
    } else if (key_value[1] !== undefined) {
      regexEmail(key_value[1]);
    }
    return key_value.join('=');
  });

  if (path !== '') {
    return regexEmail(`${path}?${queryArray.join('&')}`);
  }

  return regexEmail(queryArray.join('&'));
}

function regexEmail(str) {
  const filter = /[a-zA-Z0-9._\-+]+(@|%40)[a-zA-Z0-9]+\.[a-zA-Z0-9]+/g;
  return str.replace(filter, '[REDACTED]');
}

/**
 * Fields relevant to a page view
 *
 * @return {Object}
 *
 * Example: {"path":"/dashboard","referrer":"https://squareup.com/login","title":"Square Dashboard","search":""}
 */
export function getPageMetrics() {
  return {
    path: window.document.location.pathname,
    referrer: filterUrl(window.document.referrer),
    title: window.document.title,
    search: filterUrl(window.document.location.search),
    full_url: filterUrl(window.document.location.href),
    // note that we don't want window.location.hostname because that removes protocol (http/s) and port (if present), which are useful
    base_url: window.document.location.origin
  };
}

/**
 * Fields that represent screen size
 *
 * @return {Object}
 */
export function getScreenMetrics() {
  return {
    device_screen_height: window.screen.height,
    device_screen_width: window.screen.width,
    screen_height: window.screen.height,
    screen_width: window.screen.width
  };
}

/**
 * Fields that represent browser and screen size for V2
 *
 * @return {Object}
 */
export function getScreenMetricsV2() {
  return {
    device_screen_height: window.screen.height,
    device_screen_width: window.screen.width,
    browser_height: window.innerHeight,
    browser_width: window.innerWidth
  };
}

/**
 * Return browser name, versions, and operating system
 *
 * @return {Object}
 */
export function getBrowserAndOSMetrics() {
  const metrics = UAParser(window.navigator.userAgent);
  return {
    browser_name: metrics.browser.name,
    browser_version: metrics.browser.version,
    browser_major_version: metrics.browser.major,
    os_name: metrics.os.name,
    os_version: metrics.os.version
  };
}

/**
 * Return utm_source, utm_medium, utm_campaign, utm_term, utm_content
 *
 * @return {Object}
 */
export function getUtmCampaignData() {
  const queryParams = QueryString.parse(window.document.location.search);
  const utmCampaignData = {};
  // Only add to utmCampaignData if exists
  if (queryParams.utm_source)
    utmCampaignData['utm_source'] = queryParams.utm_source;
  if (queryParams.utm_medium)
    utmCampaignData['utm_medium'] = queryParams.utm_medium;
  if (queryParams.utm_campaign)
    utmCampaignData['utm_campaign'] = queryParams.utm_campaign;
  if (queryParams.utm_term) utmCampaignData['utm_term'] = queryParams.utm_term;
  if (queryParams.utm_content)
    utmCampaignData['utm_content'] = queryParams.utm_content;

  return utmCampaignData;
}

/**
 * Get all metrics
 *
 * @return {Object}
 */
export function getMetrics() {
  return mergeObjects([
    getScreenMetrics(),
    getPageMetrics(),
    getBrowserAndOSMetrics(),
    getUtmCampaignData()
  ]);
}

/**
 * Simple cross-browser event listening
 *
 * @return {Boolean} success
 */
export function addEvent(object, type, listener, useCapture) {
  // W3C
  if (object.addEventListener) {
    object.addEventListener(type, listener, useCapture);
    return true;
  } else if (object.attachEvent) {
    // Microsoft
    return object.attachEvent(`on${type}`, listener);
  }

  // Traditional (warning: overwrites previous listener!)
  object[`on${type}`] = listener;
  return true;
}
